import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import Downloader from "@/components/public/Downloader";
import Seo from "@/components/public/Seo";
import AdSlot from "@/components/public/AdSlot";
import PlatformIcon from "@/components/public/PlatformIcon";
import ToolContent from "@/components/public/ToolContent";
import NotFoundPage from "@/pages/NotFoundPage";
import { fetchPublishedToolSeo } from "@/lib/api";
import arabicToolSeo from "@/content/toolSeo.ar.json";

const MIN_PUBLISHER_CONTENT_CHARS = 400;

function publisherTextLength(content) {
  if (!content || typeof content !== "object") return 0;
  const values = [
    content.intro,
    content.detailed_content,
    ...(content.sections || []).flatMap((item) => [item?.title, item?.content]),
    ...(content.how_to || []),
    ...(content.features || []),
    ...(content.limitations || []),
    ...(content.supported_content || []),
    ...(content.faq || []).flatMap((item) => [item?.question, item?.answer]),
    ...(content.troubleshooting || []).flatMap((item) => [item?.issue, item?.solution]),
  ];
  return values.reduce((total, value) => total + (typeof value === "string" ? value.trim().length : 0), 0);
}

export default function ToolPage({ initialToolSeo }) {
  const { slug } = useParams();
  const { platforms, tools } = useApp();
  const { t, lang } = useI18n();
  const initialLanguage = initialToolSeo?.language || initialToolSeo?.content?.language;
  const initialSeoKey = initialToolSeo?.slug === slug
    && initialLanguage === lang
    && (initialToolSeo?.checked === true || initialToolSeo?.content?.is_published === true)
    ? `${slug}:${lang}`
    : null;
  const [seoState, setSeoState] = useState(() => ({
    key: initialSeoKey,
    status: initialSeoKey ? "ready" : "idle",
    content: initialSeoKey && initialToolSeo?.content?.is_published === true ? initialToolSeo.content : null,
  }));
  const requestedSeoKey = useRef(initialSeoKey);

  const tool = tools.find((x) => x.slug === slug);
  const platform =
    platforms.find((p) => p.slug === slug) ||
    (tool ? platforms.find((p) => p.key === tool.platform_key) : null);
  const isIndexable = Boolean(
    platform && platform.enabled && platform.supported !== false && tool && tool.enabled !== false
  );
  const toolSlug = tool?.slug;
  const requestKey = toolSlug ? `${toolSlug}:${lang}` : null;

  useEffect(() => {
    if (!toolSlug || !isIndexable) {
      requestedSeoKey.current = null;
      setSeoState({ key: requestKey, status: "ready", content: null });
      return undefined;
    }
    if (requestedSeoKey.current === requestKey) return undefined;
    requestedSeoKey.current = requestKey;
    let active = true;
    setSeoState({ key: requestKey, status: "loading", content: null });
    fetchPublishedToolSeo(toolSlug, lang)
      .then((content) => {
        if (active) setSeoState({
          key: requestKey,
          status: "ready",
          content: content?.is_published === true ? content : null,
        });
      })
      .catch(() => {
        if (active) setSeoState({ key: requestKey, status: "ready", content: null });
      });
    return () => { active = false; };
  }, [toolSlug, lang, isIndexable, requestKey]);

  if (!platform) return <NotFoundPage />;

  const seoReady = !isIndexable || (seoState.key === requestKey && seoState.status === "ready");
  const publishedSeoContent = seoReady && seoState.key === requestKey ? seoState.content : null;
  const fallbackContent = seoReady && !publishedSeoContent && lang === "ar" && isIndexable
    ? arabicToolSeo[platform.key]
    : null;
  const seoContent = publishedSeoContent || fallbackContent;
  const primaryHeading = tool?.title || `تحميل ${platform.name}`;
  const seoHeading = publishedSeoContent?.heading || fallbackContent?.heading;
  const hasDistinctSeoHeading = typeof seoHeading === "string"
    && seoHeading.trim()
    && seoHeading.trim() !== primaryHeading.trim();
  const description = isIndexable
    ? publishedSeoContent?.seo_description || fallbackContent?.description || tool?.seo_description || tool?.description || platform.description
    : (lang === "ar"
      ? `صفحة ${platform.name} محفوظة للتعريف فقط. خدمات تحليل الروابط وتنزيل الوسائط متوقفة حالياً.`
      : `${platform.name} information page only. Link analysis and media downloads are currently unavailable.`);
  const title = isIndexable
    ? publishedSeoContent?.seo_title || fallbackContent?.title || tool?.seo_title || `${seoHeading || primaryHeading} — ARAN TOOLS`
    : (lang === "ar" ? `${platform.name} — غير متاح حالياً — ARAN TOOLS` : `${platform.name} — Currently unavailable — ARAN TOOLS`);
  const visibleContent = publishedSeoContent || fallbackContent;
  const hasPublisherContent = publisherTextLength(visibleContent) >= MIN_PUBLISHER_CONTENT_CHARS;

  const completeFaq = (seoContent?.faq || []).filter((item) =>
    typeof item?.question === "string" && item.question.trim() &&
    typeof item?.answer === "string" && item.answer.trim()
  );
  const jsonLd = isIndexable ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: primaryHeading,
        applicationCategory: "MultimediaApplication",
        operatingSystem: "Web",
      },
      ...(completeFaq.length ? [{
        "@type": "FAQPage",
        mainEntity: completeFaq.map(({ question, answer }) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: { "@type": "Answer", text: answer },
        })),
      }] : []),
    ],
  } : undefined;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-4">
      <Seo
        title={title}
        description={description}
        path={`/${slug}`}
        image={platform.thumbnail}
        canonicalUrl={publishedSeoContent?.canonical_url}
        socialTitle={publishedSeoContent?.social_title}
        socialDescription={publishedSeoContent?.social_description}
        socialImage={publishedSeoContent?.social_image}
        jsonLd={jsonLd}
        noIndex={!isIndexable}
      />

      <div className="text-center mb-8">
        <span
          className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 animate-float"
          style={{ backgroundColor: `${platform.color}1a`, color: platform.color, border: `1px solid ${platform.color}33` }}
        >
          <PlatformIcon icon={platform.icon} className="w-8 h-8" />
        </span>
        {seoReady ? (
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">{primaryHeading}</h1>
        ) : (
          <div className="mx-auto h-10 w-2/3 max-w-md animate-pulse rounded-lg bg-muted" role="status" aria-label={lang === "ar" ? "جاري تحميل محتوى الأداة" : "Loading tool content"} data-testid="tool-seo-loading" />
        )}
      </div>

      {isIndexable ? (
        <Downloader platform={platform} />
      ) : (
        <div
          className="aran-card border-amber-400/30 bg-amber-400/5 p-6 text-center"
          data-testid="platform-unavailable"
        >
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-amber-400/10 text-amber-400 mb-4">
            <AlertTriangle className="w-6 h-6" />
          </span>
          <h2 className="font-head font-bold text-lg mb-2">{t("platform_unavailable")}</h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            {platform.unavailable_reason || t("temporarily_unavailable")}
          </p>
          <Link
            to="/"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            <ArrowRight className="w-4 h-4" />
            {t("home")}
          </Link>
        </div>
      )}

      {isIndexable && seoReady && visibleContent && (
        <div className="mt-8" data-testid="tool-seo-content">
          {hasDistinctSeoHeading && (
            <h2 className="mb-5 font-head text-2xl font-bold" data-testid="tool-seo-heading">{seoHeading}</h2>
          )}
          <ToolContent content={visibleContent} tools={tools} lang={lang} />
        </div>
      )}
      {isIndexable && seoReady && hasPublisherContent && <AdSlot slotKey="BETWEEN_CONTENT" />}
    </div>
  );
}
